import { ShieldCheck, GraduationCap, Stethoscope, FlaskConical, CheckCircle2, XCircle, ScrollText } from "lucide-react";
import { Link } from "react-router-dom";

export default function Compliance() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="compliance-page">
      <div className="mb-10">
        <span className="badge-sage inline-flex items-center gap-1"><ScrollText className="w-3 h-3" /> Regulatory Transparency</span>
        <h1 className="mt-4 font-serif text-4xl sm:text-5xl font-bold text-[#1A201C] tracking-tight">
          Compliance & Regulatory Framework
        </h1>
        <p className="mt-3 text-[#5A6B5D] max-w-3xl">
          Healthion India, operated by <strong>Amiroop Healthion LLP</strong>, is a pure multi-vendor OTC marketplace for
          Ayurvedic, herbal, organic and holistic wellness products. This page explains — with full transparency — the exact
          regulatory posture of the platform, its owners, and its sellers.
        </p>
      </div>

      {/* Our Posture */}
      <section className="bg-[#1E3A2B] text-[#FAF8F5] rounded-3xl p-8 sm:p-10 mb-10">
        <div className="text-xs font-mono text-[#D97706] uppercase tracking-wider">Our Operating Model</div>
        <h2 className="mt-3 font-serif text-3xl font-bold">Marketplace only. No manufacturing. No consultation. No prescriptions.</h2>
        <p className="mt-4 opacity-85 leading-relaxed max-w-3xl">
          Amiroop Healthion LLP does not manufacture Ayurvedic medicines, does not offer doctor consultations, and does not
          dispense prescription-only (Schedule E1) formulations. We curate and market <strong>Over-the-Counter (OTC) wellness
          products</strong> from verified Indian brands — so we operate under the OTC/wellness retail track of Indian
          regulation.
        </p>
        <div className="mt-6 grid sm:grid-cols-3 gap-3">
          <BadgeLine icon={CheckCircle2} label="Platform GST registered" />
          <BadgeLine icon={ShieldCheck} label="Vendor FSSAI verified" />
          <BadgeLine icon={ShieldCheck} label="Vendor AYUSH verified" />
        </div>
      </section>

      {/* When BAMS IS required */}
      <section className="mb-10">
        <h2 className="font-serif text-3xl font-bold text-[#1E3A2B]">1. Where BAMS Degree & Council Registration <em>are</em> mandatory</h2>
        <p className="mt-2 text-sm text-[#5A6B5D]">These verticals require licensed practitioners — Healthion India does <strong>not</strong> operate any of them.</p>

        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <RequirementCard
            icon={Stethoscope}
            title="Online Teleconsultation & Diagnostics"
            body="Direct doctor consultations, personalised health advice, or medical prescriptions require every consulting practitioner to hold a recognised BAMS / MD (Ayurveda) degree and an active registration with the NCISM or the relevant State Ayurvedic Council."
          />
          <RequirementCard
            icon={ScrollText}
            title="Prescription-Only Formulations (Schedule E1)"
            body="Ayurvedic medicines containing heavy metals, herbo-mineral compounds (Bhasmas, Rasa Shastra), or regulated plant extracts require a Registered Medical Practitioner's (RMP) prescription to be dispensed."
          />
          <RequirementCard
            icon={FlaskConical}
            title="AYUSH Manufacturing Licensing"
            body="Companies manufacturing their own proprietary or classical Ayurvedic medicines must appoint a qualified technical supervisor holding a BAMS or B.Pharm (Ayurveda) degree on the manufacturing premises."
          />
        </div>
      </section>

      {/* When BAMS is NOT required */}
      <section className="mb-10">
        <h2 className="font-serif text-3xl font-bold text-[#1E3A2B]">2. Where a BAMS degree is <em>not</em> required (Marketplace & OTC Sales)</h2>
        <p className="mt-2 text-sm text-[#5A6B5D]">This is Healthion India's exact operational track.</p>

        <div className="mt-6 bg-[#E8F0EA] border border-[#1E3A2B]/20 rounded-3xl p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <GraduationCap className="w-6 h-6 text-[#1E3A2B] shrink-0 mt-1" />
            <div>
              <h3 className="font-serif text-2xl font-semibold text-[#1E3A2B]">Over-the-Counter (OTC) & Wellness Retailing</h3>
              <p className="mt-2 text-[15px] text-[#1A201C] leading-relaxed">
                Operating a pure multi-vendor e-commerce marketplace selling general wellness, cosmetic, dietary, and traditional
                OTC products (e.g., Chyawanprash, herbal juices, skin care, hair oils, single-herb supplements) does <strong>not</strong>
                require the marketplace owners (Amiroop Healthion LLP) or the sellers to be licensed physicians.
              </p>
              <div className="mt-4 bg-white border border-[#E2DDD5] rounded-xl p-4">
                <div className="text-xs font-mono uppercase text-[#D97706]">Compliance requirement</div>
                <p className="text-sm mt-1 text-[#1A201C]">
                  The underlying requirement is that the <strong>original manufacturer</strong> holds a valid AYUSH Manufacturing
                  License or FSSAI License, and the products comply with standard labeling and packaging regulations. Every
                  vendor on Healthion India is manually verified for exactly these credentials before going live.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Summary Matrix */}
      <section>
        <h2 className="font-serif text-3xl font-bold text-[#1A201C]">Summary Matrix — Healthion India</h2>
        <div className="mt-6 overflow-x-auto bg-white border border-[#E2DDD5] rounded-3xl">
          <table className="w-full text-sm border-collapse min-w-[720px]">
            <thead>
              <tr className="bg-[#1E3A2B] text-[#FAF8F5]">
                <th className="text-left p-4 font-serif font-semibold">Business Vertical</th>
                <th className="text-left p-4 font-serif font-semibold">Degree & Registration Requirement</th>
                <th className="text-left p-4 font-serif font-semibold">Legal Requirement</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-[#E2DDD5] bg-[#E8F0EA]" data-testid="matrix-row-marketplace">
                <td className="p-4 font-semibold align-top">
                  <div className="flex items-center gap-2 text-[#1E3A2B]"><CheckCircle2 className="w-4 h-4" /> Multi-Vendor OTC Marketplace</div>
                  <div className="text-[10px] font-mono uppercase text-[#1E3A2B]/70 mt-1">← Healthion India</div>
                </td>
                <td className="p-4 align-top">Not required for platform owners or third-party sellers</td>
                <td className="p-4 align-top">Platform GST, vendor FSSAI / AYUSH license verification</td>
              </tr>
              <tr className="border-b border-[#E2DDD5]">
                <td className="p-4 font-semibold align-top">
                  <div className="flex items-center gap-2 text-[#5A6B5D]"><XCircle className="w-4 h-4" /> Doctor Consultation Portal</div>
                  <div className="text-[10px] font-mono uppercase text-[#5A6B5D] mt-1">Not offered</div>
                </td>
                <td className="p-4 align-top">Mandatory BAMS / MD + State / NCISM Registration</td>
                <td className="p-4 align-top">Telemedicine Practice Guidelines compliance</td>
              </tr>
              <tr>
                <td className="p-4 font-semibold align-top">
                  <div className="flex items-center gap-2 text-[#5A6B5D]"><XCircle className="w-4 h-4" /> Private Label Manufacturing</div>
                  <div className="text-[10px] font-mono uppercase text-[#5A6B5D] mt-1">Not offered</div>
                </td>
                <td className="p-4 align-top">Mandatory Technical Expert (BAMS or B.Pharm Ayurveda)</td>
                <td className="p-4 align-top">State AYUSH GMP & Manufacturing License</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="mt-6 text-xs text-[#5A6B5D] leading-relaxed">
          Disclaimer · This summary reflects Amiroop Healthion LLP's current regulatory understanding as a marketplace operator.
          Products listed on Healthion India are <strong>not</strong> intended to diagnose, treat, cure, or prevent any disease.
          Customers with medical conditions should consult a Registered Medical Practitioner before use. Vendors bear sole
          responsibility for their manufacturing licenses, labeling accuracy, and regulatory compliance per the
          {" "}<Link to="/vendor/agreement" className="text-[#1E3A2B] font-semibold underline">Marketplace Vendor Agreement</Link>.
        </p>
      </section>
    </div>
  );
}

function BadgeLine({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-3 py-2 text-xs">
      <Icon className="w-3.5 h-3.5 text-[#D97706]" /> <span className="font-mono">{label}</span>
    </div>
  );
}

function RequirementCard({ icon: Icon, title, body }) {
  return (
    <div className="card-warm p-5 h-full flex flex-col">
      <Icon className="w-6 h-6 text-[#D97706]" />
      <div className="mt-3 font-serif text-lg font-semibold text-[#1A201C]">{title}</div>
      <p className="mt-2 text-sm text-[#5A6B5D] leading-relaxed flex-1">{body}</p>
    </div>
  );
}
